import React, { useRef } from 'react';
import './Industries.css';
import { Factory, Heart, Plane, Truck, Building, ShoppingBag, HardHat, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import manufacturingBg from '../assets/Industries/Manufacturing.webp';
import healthcareBg from '../assets/Industries/Healthcare.webp';
import airportsBg from '../assets/Industries/Airports.webp';
import logisticsBg from '../assets/Industries/Logistics.webp';
import realEstateBg from '../assets/Industries/Real Estate.webp';
import constructionBg from '../assets/Industries/Construction.webp';
import warehousingBg from '../assets/Industries/Warehousing.webp';

interface IndustryData {
  name: string;
  tagline: string;
  icon: React.ReactNode;
  accentColor: string;
  image: string;
}

interface IndustriesProps {
  preview?: boolean;
}

const industriesList: IndustryData[] = [
  {
    name: 'Manufacturing',
    tagline: 'IoT Monitoring & Smart Automation',
    icon: <Factory size={28} />,
    accentColor: '#3B82F6',
    image: manufacturingBg
  },
  {
    name: 'Healthcare',
    tagline: 'AI Diagnostics & Patient Care',
    icon: <Heart size={28} />,
    accentColor: '#3B82F6',
    image: healthcareBg
  },
  {
    name: 'Airports',
    tagline: 'Computer Vision & Smart Security',
    icon: <Plane size={28} />,
    accentColor: '#3B82F6',
    image: airportsBg
  },
  {
    name: 'Logistics',
    tagline: 'Warehouse Automation & Fleet Management',
    icon: <Truck size={28} />,
    accentColor: '#3B82F6',
    image: logisticsBg
  },
  {
    name: 'Real Estate',
    tagline: 'Property Management & Digital Contracts',
    icon: <Building size={28} />,
    accentColor: '#3B82F6',
    image: realEstateBg
  },
  {
    name: 'Retail',
    tagline: 'Inventory Management & POS Automation',
    icon: <ShoppingBag size={28} />,
    accentColor: '#3B82F6',
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=400&q=80'
  },
  {
    name: 'Construction',
    tagline: 'Construction Safety & Equipment Monitoring',
    icon: <HardHat size={28} />,
    accentColor: '#3B82F6',
    image: constructionBg
  },
  {
    name: 'Warehouse',
    tagline: 'Warehouse Automation & Inventory Management',
    icon: <Package size={28} />,
    accentColor: '#3B82F6',
    image: warehousingBg
  }
];

const IndustryCard: React.FC<{ ind: IndustryData }> = ({ ind }) => (
  <div
    className="industry-card"
    style={{ 
      '--industry-accent': ind.accentColor,
      backgroundImage: `linear-gradient(rgba(18, 21, 28, 0.6), rgba(18, 21, 28, 0.9)), url("${ind.image}")`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      border: '1px solid rgba(255, 255, 255, 0.05)'
    } as React.CSSProperties}
  >
    <div className="industry-card-header-row">
      <div className="industry-card-icon-box">
        {ind.icon}
      </div>
      <h3 className="industry-card-name">{ind.name}</h3>
    </div>
    <p className="industry-card-tagline">{ind.tagline}</p>
    <Link to="/industries" className="industry-card-btn">
      Learn more
    </Link>
  </div>
);

export const Industries: React.FC<IndustriesProps> = ({ preview: _preview = false }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 304; // Width of card (280) + gap (24)
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="section industries-section" id="industries">
      <div className="container">
        <div className="section-header centered">
          <h2 className="section-title">Transforming Industries with Technology</h2>
          <p>We tailor our intelligent software products and workflow systems to meet the security, compliance, and regulatory requirements of global industries.</p>
        </div>

        {/* Scrollable horizontal cards container with arrow buttons */}
        <div className="industries-scroll-wrapper-outer">
          <button className="industries-scroll-btn left" onClick={() => scroll('left')} aria-label="Scroll left">
            <ChevronLeft size={20} />
          </button>
          
          <div className="industries-scroll-wrapper">
            <div className="industries-scroll-container" ref={scrollContainerRef}>
              {industriesList.map((ind, idx) => (
                <IndustryCard key={idx} ind={ind} />
              ))}
            </div>
          </div>

          <button className="industries-scroll-btn right" onClick={() => scroll('right')} aria-label="Scroll right">
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Industries;
